import { Download, FileText, Table, FileJson, Bookmark } from "lucide-react";
import { GeneratedDork } from "@/types/dork";
import { toast } from "@/hooks/use-toast";
import { saveDorksToLibrary } from "./SavedDorksLibrary";
import { useLocale } from "@/contexts/LocaleContext";

interface ExportOptionsProps {
  dorks: GeneratedDork[];
  keyword: string;
}

export function ExportOptions({ dorks, keyword }: ExportOptionsProps) {
  const { t } = useLocale();

  const exportAsTxt = () => {
    const header = `# Google Dorks Generated for: "${keyword}"
# Generated: ${new Date().toISOString()}
# For educational and authorized security research only
# ================================================

`;
    const content = dorks
      .map((d, i) => `[${i + 1}] ${d.category.toUpperCase()} (Effectiveness: ${"★".repeat(d.effectiveness)}${"☆".repeat(5 - d.effectiveness)})
Query: ${d.query}
Purpose: ${d.purpose}
Operators: ${d.operators.join(", ")}
`)
      .join("\n");
    
    const blob = new Blob([header + content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dorks-${keyword.replace(/\s+/g, "-")}-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast({
      title: t("export.exported_title"),
      description: t("export.txt_desc"),
    });
  };

  const exportAsCsv = () => {
    const headers = ["Query", "Purpose", "Category", "Effectiveness", "Operators"];
    const rows = dorks.map((d) => [
      `"${d.query.replace(/"/g, '""')}"`,
      `"${d.purpose.replace(/"/g, '""')}"`,
      d.category,
      d.effectiveness,
      `"${d.operators.join(", ")}"`,
    ]);
    
    const content = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    
    const blob = new Blob([content], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dorks-${keyword.replace(/\s+/g, "-")}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast({
      title: t("export.exported_title"),
      description: t("export.csv_desc"),
    });
  };

  const exportAsJson = () => {
    const data = {
      keyword,
      generatedAt: new Date().toISOString(),
      disclaimer: "For educational, research, and authorized security testing purposes only",
      dorks: dorks.map((d) => ({
        query: d.query,
        purpose: d.purpose,
        category: d.category,
        effectiveness: d.effectiveness,
        operators: d.operators,
      })),
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dorks-${keyword.replace(/\s+/g, "-")}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast({
      title: t("export.exported_title"),
      description: t("export.json_desc"),
    });
  };

  const saveToLibrary = () => {
    saveDorksToLibrary(keyword, dorks);
    toast({
      title: t("export.saved_title"),
      description: t("export.saved_desc"),
    });
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        onClick={saveToLibrary}
        className="flex items-center gap-2 px-3 py-2 text-xs font-medium bg-primary/10 hover:bg-primary/20 text-primary rounded-lg border border-primary/30 transition-colors"
      >
        <Bookmark className="w-3.5 h-3.5" />
        {t("export.save")}
      </button>
      <button
        onClick={exportAsTxt}
        className="flex items-center gap-2 px-3 py-2 text-xs font-medium bg-muted/30 hover:bg-muted/50 text-muted-foreground hover:text-foreground rounded-lg border border-border/30 transition-colors"
      >
        <FileText className="w-3.5 h-3.5" />
        TXT
      </button>
      <button
        onClick={exportAsCsv}
        className="flex items-center gap-2 px-3 py-2 text-xs font-medium bg-muted/30 hover:bg-muted/50 text-muted-foreground hover:text-foreground rounded-lg border border-border/30 transition-colors"
      >
        <Table className="w-3.5 h-3.5" />
        CSV
      </button>
      <button
        onClick={exportAsJson}
        className="flex items-center gap-2 px-3 py-2 text-xs font-medium bg-muted/30 hover:bg-muted/50 text-muted-foreground hover:text-foreground rounded-lg border border-border/30 transition-colors"
      >
        <FileJson className="w-3.5 h-3.5" />
        JSON
      </button>
    </div>
  );
}
